import { Box, Paper, Typography, InputAdornment } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/Delete';
import AddBoxIcon from '@material-ui/icons/AddBox';
import arrayMove from 'array-move';
import React, { useEffect, useRef, useState } from 'react';
import { SortableContainer as sortableContainer, SortableElement as sortableElement } from 'react-sortable-hoc';

import FormTextField, { IFormTextFieldImperativeHandle } from '../../../components/FormTextField';
import ObjectSelector from '../../../components/ObjectSelector';
import { SubmitControl } from '../../../components/SubmitControl';
import { useFlow, useFlowMutation } from '../../../hooks/queries/useOnboarding';
import useConfirmDialog from '../../../hooks/useConfirmDialog';
import { fetchLane } from '../../../services/api/Onboarding';
import { ICollection, IOnboardingFlow, IOnboardingFlowLaneMapper, IOnboardingLane } from '../../../services/types';
import FlowItem from './LaneItem';
import { contentTypeIcon } from '../../../components/StoryItem';

interface IProps {
  flowId?: number;
  onCreate?: (flow: IOnboardingFlow) => void;
}

enum ICollectionEditType {
  Mature = 'mature',
  NonMature = 'nonmature',
  None = '',
}

const useStyles = makeStyles((theme: Theme) => ({
  infoWrap: {
    marginBottom: 30,
    minHeight: 285,
  },
  infoLeft: {
    padding: '30px 25px 0 25px',
    display: 'flex',
    flex: 1,
  },
  infoLeftLeft: { flex: 1, paddingRight: 25 },
  collectionEdit: { flex: 1 },
  inputLabel: {
    fontSize: 15,
    fontWeight: 'normal',
    color: '#212121',
  },
  imageUploader: {
    marginTop: 8,
    width: 414,
  },
  imageDescription: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  dualTableWrap: {
    display: 'flex',
    minHeight: 285,
  },
  dualTableLeftWrap: {
    flex: 1,
    minWidth: 0,
  },
  dualTableRightWrap: {
    flex: '0 0 320px',
  },
  dualTableRightBox: {
    borderLeft: '1px solid #dcdcdc',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  dualTableRightHeader: {
    backgroundColor: '#eee',
    padding: '15px 24px 15px',
    lineHeight: '1rem',
    borderBottom: '1px solid rgba(224, 224, 224, 1)',
  },
  dualTableRightHeaderText: {
    color: 'rgba(0,0,0, 0.54)',
    fontSize: 12,
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
  dualTableRightContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: '10px 0',
  },
  collectionLabel: {
    marginBottom: 13,
  },
  collectionsContainer: { overflow: 'hidden' },
  collectionListContainer: {},
  deleteIcon: {
    cursor: 'pointer',
  },
  helper: {
    marginLeft: -5,
    borderRadius: '4px',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    boxShadow: '0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)',
  },
}));

const createEmptyFlow = (): Partial<IOnboardingFlow> => ({ flowKey: '', flowLaneMappers: [] });

const SortableContainerBox = sortableContainer((props: any) => {
  const { children } = props;
  const classes = useStyles();

  return <Box className={classes.collectionListContainer}>{children}</Box>;
});
const SortableMapperItem = sortableElement(FlowItem);

const FlowView = (props: IProps): JSX.Element => {
  const classes = useStyles();
  const { flowId, onCreate } = props;

  const confirm = useConfirmDialog();

  // useQueries
  const { data: dataFromQuery, isLoading } = useFlow(flowId);
  const { create, update } = useFlowMutation();

  // useRefs
  const titleRef = useRef<IFormTextFieldImperativeHandle>();
  const flowKeyRef = useRef<IFormTextFieldImperativeHandle>();
  const matureCollectionRef = useRef<IFormTextFieldImperativeHandle>();
  const nonMatureCollectionRef = useRef<IFormTextFieldImperativeHandle>();

  // useStates
  const [flow, setFlow] = useState<IOnboardingFlow | Partial<IOnboardingFlow>>(createEmptyFlow());
  const [collectionEditType, setCollectionEditType] = useState<ICollectionEditType>(ICollectionEditType.None);
  const [collectionSelectedIds, setCollectionSelectedIds] = useState<number[]>([0]);

  // form states
  const [mode, setMode] = useState<'create' | 'edit' | 'view'>(flowId ? 'view' : 'create');
  const isEditing = mode !== 'view';
  const getValidationMessages = () => {
    const messages = [
      ...(titleRef.current?.getValidationMessages() || []),
      ...(flowKeyRef.current?.getValidationMessages() || []),
      ...(matureCollectionRef.current?.getValidationMessages() || []),
      ...(nonMatureCollectionRef.current?.getValidationMessages() || []),
    ];

    return messages;
  };

  // handlers
  const handleEditClick = () => {
    setMode('edit');
  };

  const handleCancelClick = () => {
    const hasChanges = JSON.stringify(dataFromQuery) !== JSON.stringify(flow);

    if (hasChanges) {
      confirm.open({
        title: 'Wait!',
        text: 'You have unsaved changes. Are you sure you want to discard?',
        onProceed: () => {
          reset();
        },
      });
    } else {
      reset();
    }
  };

  const reset = () => {
    if (mode === 'edit' && dataFromQuery) {
      setFlow(dataFromQuery);
      setMode('view');
    } else {
      setFlow(createEmptyFlow());
    }
    setCollectionEditType(ICollectionEditType.None);
  };

  const handleSaveClick = async () => {
    // remove unnecessary data. api post data size limit.
    const purifiedFlow = {
      ...flow,
      defaultMatureCollection: undefined,
      defaultNonMatureCollection: undefined,
      flowLaneMappers: flow.flowLaneMappers?.map(mapper => ({ laneId: mapper.laneId, priority: mapper.priority })),
    };
    const result = await (flowId ? update : create).mutateAsync(purifiedFlow);
    if (mode === 'create') {
      reset();
    } else {
      setFlow({ ...flow, ...result });
    }
    if (mode === 'edit') {
      setMode('view');
    }
    if (onCreate) {
      onCreate(result);
    }
  };

  const handleCollectionDelete = (editType: ICollectionEditType) => {
    if (editType === ICollectionEditType.Mature) {
      setFlow(ps => ({ ...ps, defaultMatureTrackingId: null, defaultMatureCollection: null }));
    } else if (editType === ICollectionEditType.NonMature) {
      setFlow(ps => ({ ...ps, defaultNonMatureTrackingId: null, defaultNonMatureCollection: null }));
    }
    setCollectionEditType(editType);
  };

  const handleCollectionAdd = async (collection: ICollection) => {
    if (collectionEditType === ICollectionEditType.Mature) {
      setFlow(ps => ({ ...ps, defaultMatureTrackingId: collection.trackingId, defaultMatureCollection: collection }));
      matureCollectionRef.current?.clearValidationMessage();
    } else if (collectionEditType === ICollectionEditType.NonMature) {
      setFlow(ps => ({ ...ps, defaultNonMatureTrackingId: collection.trackingId, defaultNonMatureCollection: collection }));
      nonMatureCollectionRef.current?.clearValidationMessage();
    }
  };

  const handleLaneAdd = async (lane: IOnboardingLane) => {
    const [fetchPromise] = fetchLane(lane.id!);
    const fetchedLane = await fetchPromise;
    const newMapper: Partial<IOnboardingFlowLaneMapper> = { lane: fetchedLane.data, laneId: lane.id, priority: flow.flowLaneMappers!.length + 1 };
    setFlow(ps => ({ ...ps, flowLaneMappers: [...ps.flowLaneMappers!, newMapper] }));
  };

  const handleSortEnd = ({ newIndex, oldIndex }: { newIndex: number; oldIndex: number }) => {
    const sorted = arrayMove([...flow.flowLaneMappers!], oldIndex, newIndex);
    sorted.forEach((t, i) => (t.priority = i + 1));
    setFlow(ps => ({ ...ps, flowLaneMappers: sorted }));
  };

  const handleDefaultCollectionFocus = (type: ICollectionEditType) => {
    setTimeout(() => setCollectionEditType(type), type === ICollectionEditType.None ? 300 : 0);
  };

  // useEffects
  useEffect(() => {
    // replace initial state to loaded data
    if (dataFromQuery) {
      setFlow(dataFromQuery);
    }
  }, [dataFromQuery]);

  useEffect(() => {
    if (collectionEditType === ICollectionEditType.Mature) {
      setCollectionSelectedIds(flow.defaultMatureCollection ? [flow.defaultMatureCollection.id!] : []);
    } else if (collectionEditType === ICollectionEditType.NonMature) {
      setCollectionSelectedIds(flow.defaultNonMatureCollection ? [flow.defaultNonMatureCollection.id!] : []);
    } else {
      setCollectionSelectedIds([0]);
    }
  }, [flow, collectionEditType]);

  return (
    <Box width={'1080px'}>
      <Box display="flex" alignItems="center" paddingBottom={2}>
        <Typography variant="h6">{mode === 'create' ? 'Create an Onboarding Flow' : flow?.title || ''}</Typography>
        <SubmitControl
          mode={mode}
          onEditClick={handleEditClick}
          validationMessages={getValidationMessages()}
          onSubmitClick={handleSaveClick}
          onCancelClick={handleCancelClick}
        />
      </Box>
      <Paper className={classes.infoWrap}>
        <Box className={classes.dualTableWrap}>
          <Box className={classes.dualTableLeftWrap}>
            <Box className={classes.infoLeft}>
              <Box className={classes.infoLeftLeft}>
                <Box marginBottom={'30px'}>
                  <Typography className={''}>Flow Title</Typography>
                  <FormTextField
                    defaultValue={flow.title}
                    name="Title"
                    placeholder="Add title"
                    isEditing={isEditing}
                    isLoading={isLoading}
                    required={true}
                    fullWidth
                    ref={titleRef}
                    onValueSubmit={(title: string) => {
                      setFlow(ps => ({ ...ps, title }));
                    }}
                    className={''}
                  />
                </Box>
                <Box marginBottom={'30px'}>
                  <Typography className={''}>Deeplink Flow Key</Typography>
                  <FormTextField
                    defaultValue={flow.flowKey}
                    name="Flow Key"
                    placeholder="Add flow key"
                    isEditing={isEditing}
                    isLoading={isLoading}
                    required={false}
                    fullWidth
                    ref={flowKeyRef}
                    onValueSubmit={(flowKey: string) => {
                      setFlow(ps => ({ ...ps, flowKey }));
                    }}
                    className={''}
                  />
                </Box>
              </Box>
              <Box className={classes.collectionEdit}>
                <Typography color={collectionEditType === ICollectionEditType.Mature ? 'primary' : undefined}>Mature Default Collection</Typography>
                <Box marginBottom={'30px'}>
                  <FormTextField
                    defaultValue={flow.defaultMatureCollection?.title || flow.defaultMatureTrackingId || ''}
                    required={true}
                    name="Default Collection"
                    placeholder="Add collection"
                    isEditing={isEditing}
                    isLoading={isLoading}
                    fullWidth
                    ref={matureCollectionRef}
                    className={''}
                    InputProps={{
                      readOnly: true,
                      endAdornment: flow.defaultMatureTrackingId ? (
                        <DeleteIcon
                          className={classes.deleteIcon}
                          color={'primary'}
                          onClick={() => handleCollectionDelete(ICollectionEditType.Mature)}
                        />
                      ) : null,
                      startAdornment: flow.defaultMatureTrackingId ? (
                        flow.defaultMatureCollection?.storyTypes?.map((storyType, index) => (
                          <span key={`matureCollection-${storyType}-${index}`}>{contentTypeIcon(storyType)}</span>
                        ))
                      ) : (
                        <InputAdornment position="start" style={{ marginRight: 4 }}>
                          <AddBoxIcon color={'primary'} fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    onFocus={e => {
                      handleDefaultCollectionFocus(ICollectionEditType.Mature);
                    }}
                  />
                </Box>
                <Typography color={collectionEditType === ICollectionEditType.NonMature ? 'primary' : undefined}>
                  Non-mature Default Collection
                </Typography>
                <Box>
                  <FormTextField
                    defaultValue={flow.defaultNonMatureCollection?.title || flow.defaultNonMatureTrackingId || ''}
                    required={true}
                    name="Default Non-mature Collection"
                    placeholder="Add collection"
                    isEditing={isEditing}
                    isLoading={isLoading}
                    fullWidth
                    ref={nonMatureCollectionRef}
                    className={''}
                    InputProps={{
                      readOnly: true,
                      endAdornment: flow.defaultNonMatureTrackingId ? (
                        <DeleteIcon
                          className={classes.deleteIcon}
                          color={'primary'}
                          onClick={() => handleCollectionDelete(ICollectionEditType.NonMature)}
                        />
                      ) : null,
                      startAdornment: flow.defaultNonMatureTrackingId ? (
                        flow.defaultNonMatureCollection?.storyTypes?.map((storyType, index) => (
                          <span key={`nonMatureCollection-${storyType}-${index}`}>{contentTypeIcon(storyType)}</span>
                        ))
                      ) : (
                        <InputAdornment position="start" style={{ marginRight: 4 }}>
                          <AddBoxIcon color={'primary'} fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    onFocus={() => {
                      handleDefaultCollectionFocus(ICollectionEditType.NonMature);
                    }}
                  ></FormTextField>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box className={classes.dualTableRightWrap}>
            {isEditing && (
              <Box
                className={classes.dualTableRightBox}
                // style={{ visibility: collectionEditType === ICollectionEditType.None ? 'hidden' : 'visible' }}
              >
                <ObjectSelector
                  type="collection"
                  isOnboarding={true}
                  className={classes.dualTableRightContent}
                  selectedIds={collectionSelectedIds}
                  selectLimit={1}
                  onAddCollection={handleCollectionAdd}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Paper>
      <Box marginBottom={'30px'}>
        <Typography variant="h6" className={classes.collectionLabel}>
          Lanes
        </Typography>
        <Paper className={classes.collectionsContainer}>
          <Box className={isEditing ? classes.dualTableWrap : ''}>
            <Box className={classes.dualTableLeftWrap}>
              <SortableContainerBox lockAxis="y" useDragHandle={true} onSortEnd={handleSortEnd} helperClass={classes.helper}>
                {flow?.flowLaneMappers?.map((laneMapper, i) => (
                  <SortableMapperItem
                    index={i}
                    disabled={!isEditing}
                    key={`flow-lane-mapper-item-${laneMapper.lane!.id}`}
                    flowLaneMapper={laneMapper}
                    isEditing={isEditing}
                    onChange={(mapper: Partial<IOnboardingFlowLaneMapper>) => {
                      const newMappers = [...flow.flowLaneMappers!];
                      newMappers[i] = mapper;
                      setFlow(ps => ({ ...ps, flowLaneMappers: newMappers }));
                    }}
                    onDelete={() => {
                      const newMappers = [...flow.flowLaneMappers!];
                      newMappers.splice(i, 1);
                      setFlow(ps => ({ ...ps, flowLaneMappers: newMappers }));
                    }}
                  />
                ))}
              </SortableContainerBox>
            </Box>
            {isEditing && (
              <Box className={classes.dualTableRightWrap}>
                <Box className={classes.dualTableRightBox}>
                  <ObjectSelector
                    type="lane"
                    className={classes.dualTableRightContent}
                    selectedIds={flow.flowLaneMappers?.map(mapper => mapper.laneId!) || []}
                    selectLimit={50}
                    onAddObject={lane => {
                      handleLaneAdd(lane as IOnboardingLane);
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default FlowView;
